import { FC, useState, useEffect } from 'react';
import { Bell, Check, Trash2, Filter } from 'lucide-react';
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { useNotifications } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getNotificationStyle } from '../notifications/notificationStyles';
import { NotificationListener } from '../notifications/NotificationListener';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/lib/supabase";
import { useToast } from '@/components/ui/use-toast';

type NotificationType = 'all' | 'new_content' | 'new_game' | 'achievement' | 'community_post' | 'comment' | 'message' | 'system';

export const NotificationsMenu: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const { toast } = useToast();
  const [filterType, setFilterType] = useState<NotificationType>('all');
  const [isOpen, setIsOpen] = useState(false);

  const handleNotificationClick = async (notification: any) => {
    await markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error al eliminar notificación:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "No se pudo eliminar la notificación",
        });
        return;
      }

      // Invalidar la consulta para refrescar la lista
      queryClient.invalidateQueries(['notifications', user?.id]);

      toast({
        title: "Notificación eliminada",
        description: "La notificación ha sido eliminada exitosamente",
      });
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar la notificación",
      });
    }
  };

  const filteredNotifications = notifications.filter(n => 
    filterType === 'all' || n.type === filterType
  );

  const translateAction = (action: string) => {
    const actionTranslations: { [key: string]: string } = {
      'Content reviewed and action taken': 'Contenido revisado y acción tomada',
      'Content hidden': 'Contenido ocultado',
      'Content deleted': 'Contenido eliminado',
      'Warning issued': 'Advertencia emitida',
      'No action needed': 'No se requiere acción'
    };
    return actionTranslations[action] || action;
  };

  // Forzar recarga de notificaciones cuando el usuario inicia sesión
  useEffect(() => {
    if (user?.id) {
      // Invalidar la caché de notificaciones para forzar una recarga
      queryClient.invalidateQueries(['notifications', user.id]);
    }
  }, [user?.id, queryClient]);

  return (
    <>
      <NotificationListener />
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <motion.div 
            className="p-2 hover:bg-primary/10 rounded-full transition-colors cursor-pointer relative"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Bell className="h-6 w-6 text-primary" />
            <AnimatePresence>
              {unreadCount > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                >
                  {unreadCount}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 bg-white/95 backdrop-blur-sm border-2 border-primary/20 shadow-xl rounded-xl">
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-primary/10 bg-gradient-to-r from-primary/5 to-accent/5">
            <DropdownMenuLabel className="font-bold text-lg text-primary">
              Notificaciones
            </DropdownMenuLabel>
            <div className="flex gap-2">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="h-8 w-8 p-0">
                  <Filter className="h-4 w-4" />
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                    <DropdownMenuRadioItem value="all">Todas</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="new_content">Contenido</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="new_game">Juegos</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="achievement">Logros</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="community_post">Comunidad</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="comment">Comentarios</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="message">Mensajes</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              {unreadCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => markAllAsRead()}
                  className="h-8 w-8 p-0"
                >
                  <Check className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <ScrollArea className="max-h-[400px]">
            {filteredNotifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No tienes notificaciones
                {filterType !== 'all' && ' de este tipo'}
              </div>
            ) : (
              <AnimatePresence>
                {filteredNotifications.map((notification) => {
                  const style = getNotificationStyle(notification.type);
                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.2 }}
                    >
                      <DropdownMenuItem
                        className={`
                          cursor-pointer hover:bg-primary/5 focus:bg-primary/5 px-4 py-3 
                          transition-colors rounded-lg m-1 relative group
                          ${!notification.read ? `${style.bgColor} ${style.borderColor} border-l-4` : ''}
                        `}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex gap-3 w-full">
                          <span className="text-xl">{style.icon}</span>
                          <div className="flex flex-col gap-1 flex-1">
                            <span className={`font-bold bg-gradient-to-r ${style.color} bg-clip-text text-transparent`}>
                              {notification.title}
                            </span>
                            <span className="text-sm text-gray-600">
                              {notification.metadata?.reason ? 
                                translateAction(notification.metadata.reason) :
                                notification.message
                              }
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(notification.created_at), {
                                addSuffix: true,
                                locale: es
                              })}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteNotification(notification.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                          </Button>
                        </div>
                      </DropdownMenuItem>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};